import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';

const empty = { name: '', phone: '', line: '', city: '', state: '', pincode: '' };

const Addresses = () => {
    const [addresses, setAddresses] = useState([]);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null); // null = adding new, number = editing
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState('');          // form error
    const [pageError, setPageError] = useState('');  // errors from default / remove buttons
    const [busyId, setBusyId] = useState(null);      // address id being changed

    // always get the latest list from the server
    const loadAddresses = async () => {
        const { ok, data } = await apiFetch('/address');
        if (ok && Array.isArray(data)) setAddresses(data);
    };
        
    useEffect(() => {
        let cancelled = false;
        apiFetch('/address').then(({ ok, data }) => {
            if (!cancelled && ok && Array.isArray(data)) setAddresses(data);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    
    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
    
    const closeForm = () => {
        setForm(empty);
        setEditingId(null);
        setShowForm(false);
        setError('');
        };
    
    const openAdd = () => {
        if (showForm && editingId === null) return closeForm();
        setForm(empty);
        setEditingId(null);
        setError('');
        setShowForm(true);
    };

    const openEdit = (a) => {
        setForm({ name: a.name, phone: a.phone, line: a.line, city: a.city, state: a.state, pincode: a.pincode });
        setEditingId(a.id);
        setError('');
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError('');
        const url = editingId ? `/address/${editingId}` : '/address';
        const method = editingId ? 'PUT' : 'POST';
        const { ok, data } = await apiFetch(url, { method, body: form });
        if (!ok) {
            setError(data?.message || 'Could not save the address.');
            return;
        }
        if (Array.isArray(data)) setAddresses(data); // the API returns the updated list
        else await loadAddresses();
        closeForm();
    };

    const makeDefault = async (id) => {
        setPageError('');
        setBusyId(id);
        try {
            const { ok, data } = await apiFetch(`/address/${id}/default`, { method: 'PATCH' });
            if (!ok) {
                setPageError(
                    data?.message ||
                        'Could not set the default address. Check that the PATCH /address/:id/default route exists and is allowed by CORS.'
                );
                return;
            }
            // use the list from the API, or reload it from the server
            if (Array.isArray(data)) setAddresses(data);
            else await loadAddresses();
        } catch (err) {
            setPageError('Network error. Could not set the default address.');
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Remove this address?')) return;
        setPageError('');
        setBusyId(id);
        try {
            const { ok, data } = await apiFetch(`/address/${id}`, { method: 'DELETE' });
            if (!ok) {
                setPageError(data?.message || 'Could not remove the address.');
                return;
            }
            if (Array.isArray(data)) setAddresses(data);
            else await loadAddresses();
            if (editingId === id) closeForm();
        } catch (err) {
            setPageError('Network error. Could not remove the address.');
        } finally {
            setBusyId(null);
        }
    };

    const field = 'w-full rounded-md border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2f6b1f]';

    return (
        <>
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">Addresses</h1>
                <button
                    onClick={openAdd}
                    className="rounded-md bg-[#2f6b1f] px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
                >
                    {showForm && editingId === null ? 'Cancel' : 'Add address'}
                </button>
            </div>

            {pageError && (
                <p role="alert" className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">
                    {pageError}
                </p>
            )}

            {showForm && (
                <form onSubmit={handleSave} className="mt-4 grid gap-3 rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:grid-cols-2">
                    <h2 className="text-sm font-semibold sm:col-span-2">
                        {editingId ? 'Edit address' : 'New address'}
                    </h2>
                    <input required name="name" value={form.name} onChange={handleChange} placeholder="Full name" className={field} />
                    <input required name="phone" value={form.phone} onChange={handleChange} placeholder="Mobile number" className={field} />
                    <input required name="line" value={form.line} onChange={handleChange} placeholder="House no., street, area" className={`${field} sm:col-span-2`} />
                    <input required name="city" value={form.city} onChange={handleChange} placeholder="City" className={field} />
                    <input required name="state" value={form.state} onChange={handleChange} placeholder="State" className={field} />
                    <input required name="pincode" value={form.pincode} onChange={handleChange} placeholder="Pincode (6 digits)" maxLength={6} inputMode="numeric" className={field} />
                    {error && <p role="alert" className="text-xs text-red-600 sm:col-span-2">{error}</p>}
                    <div className="flex gap-3 sm:col-span-2">
                        <button type="submit" className="flex-1 rounded-md bg-[#2f6b1f] py-2.5 text-sm font-semibold text-white">
                            {editingId ? 'Update address' : 'Save address'}
                        </button>
                        <button type="button" onClick={closeForm} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50">
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {addresses.length === 0 && <p className="text-sm text-gray-500">No saved addresses yet.</p>}
                {addresses.map((a) => {
                    const isDefault = Number(a.is_default) === 1; // works for 1, "1" and true
                    return (
                        <div
                            key={a.id}
                            className={`rounded-xl border bg-white p-5 shadow-sm ${isDefault ? 'border-[#2f6b1f]' : 'border-gray-200'}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-semibold">{a.name}</p>
                                {isDefault ? (
                                    <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-[#2f6b1f]">Default</span>
                                ) : null}
                            </div>
                            <p className="mt-1 text-sm text-gray-600">{a.line}, {a.city}, {a.state} - {a.pincode}</p>
                            <p className="mt-1 text-xs text-gray-500">{a.phone}</p>

                            <div className="mt-3 flex flex-wrap gap-4 text-xs font-medium">
                                <button onClick={() => openEdit(a)} className="text-[#2f6b1f] hover:underline">Edit</button>
                                {!isDefault && (
                                    <button
                                        onClick={() => makeDefault(a.id)}
                                        disabled={busyId === a.id}
                                        className="text-gray-700 hover:underline disabled:opacity-50"
                                    >
                                        {busyId === a.id ? 'Saving...' : 'Set as default'}
                                    </button>
                                )}
                                <button
                                    onClick={() => remove(a.id)}
                                    disabled={busyId === a.id}
                                    className="text-red-600 hover:underline disabled:opacity-50"
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default Addresses;
